import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import {
  accent9 as accent, mudraSrc9, useSlide9, Seg,
} from "./slide9.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 9 in ALL mode — flowing, scrollable, no canvas.
   Keeps the Vishnu-mudra image (the hand position is instructional).
   (The Practice-mode body version keeps the interactive nostril guide instead.) */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.7, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide9Mobile: React.FC = () => {
  const { meta: slide9Meta, steps: slide9Steps, what: slide9What, duration: slide9Duration } = useSlide9();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: "#3a9470", textTransform: "uppercase", marginBottom: 8 }}>
        {slide9Meta.breadcrumbPre} · <span style={{ color: accent }}>{slide9Meta.breadcrumbAccent}</span>
      </div>

      {/* sanskrit + title */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 3, color: "rgba(62,203,138,0.55)", marginBottom: 6 }}>{slide9Meta.sanskrit}</div>
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 25, lineHeight: 1.2, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide9Meta.titlePre}<span style={{ color: accent }}>{slide9Meta.titleAccentWord}</span>{slide9Meta.titlePost}
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: "#6aa088", margin: "0 0 20px", lineHeight: 1.5 }}>
        {slide9Meta.subtitle}
      </p>

      {/* what it does */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide9Meta.whatLabel}</div>
        <p style={txt}>{renderSegs(slide9What, "#a0f0cc")}</p>
      </div>

      {/* mudra image */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
        <img
          src={mudraSrc9}
          alt={slide9Meta.mudraAlt}
          style={{ width: "min(58%, 190px)", height: "auto", opacity: 0.92 }}
        />
      </div>

      {/* the cycle */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide9Meta.cycleLabel}</div>
        {slide9Steps.map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 10 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: accent, flexShrink: 0, marginTop: 7, opacity: 0.75 }} />
            <div>
              <span style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 700, fontSize: 14.5, color: "#b8f0d8" }}>{s.step} </span>
              <span style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14.5, lineHeight: 1.55, color: "#9bbdd6" }}>{s.desc}</span>
            </div>
          </div>
        ))}
      </div>

      {/* duration callout */}
      <div style={{ background: "rgba(62,203,138,0.06)", border: "1px solid rgba(62,203,138,0.22)", borderRadius: 10, padding: "14px 16px" }}>
        <div style={lbl}>{slide9Meta.durationLabel}</div>
        <p style={{ ...txt, color: "#a8f0d0" }}>{renderSegs(slide9Duration, "#c8ffe8")}</p>
      </div>
    </div>
  );
};

export default HTLDSlide9Mobile;
